import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { updateHeadset, getUsersInHeadquarter } from "../helpers/requests";

const ChangeHeadsetDataModal = ({ setShowChangeHeadsetDataModal, headset, handleChangeHeadsetData, headsetId }) => {
  const { t } = useTranslation();
  const [headsetName, setHeadsetName] = useState(headset.name || "");
  // Initialize admin IDs from the headset prop
  const [selectedAdminIds, setSelectedAdminIds] = useState<string[]>(headset.adminIds || []);
  const [availableAdmins, setAvailableAdmins] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingAdmins, setIsFetchingAdmins] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchAdmins = async () => {
      setIsFetchingAdmins(true);
      if (headset.headquarterId) {
        try {
          const users = await getUsersInHeadquarter(headset.headquarterId);
          const filteredAdmins = users.filter((u: any) => 
            u.role === 'headquarterAdmin' || u.role === 'headsetAdmin' || u.role === 'admin'
          );
          setAvailableAdmins(filteredAdmins);
        } catch (e) {
          console.error("Failed to fetch admins", e);
        } finally {
          setIsFetchingAdmins(false);
        }
      } else {
        setIsFetchingAdmins(false);
      }
    };
    fetchAdmins();
  }, [headset.headquarterId]);

  const handleRemoveAdmin = (id: string) => {
    setSelectedAdminIds(selectedAdminIds.filter(adminId => adminId !== id));
  };

  const getAdminDisplayName = (id: string) => {
    const user = availableAdmins.find(u => u.id === id);
    if (user) {
      return `${user.firstName} ${user.lastName} (${t(user.role)})`;
    }
    
    // Fallback
    if (headset.adminIds && headset.adminNames) {
        const index = headset.adminIds.indexOf(id);
        if (index !== -1 && headset.adminNames[index]) {
            return headset.adminNames[index];
        }
    }

    return "Unknown User (" + id + ")";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!headsetName.trim()) {
      setError(t("Please_enter_headset_name"));
      setIsLoading(false);
      return;
    }

    if (selectedAdminIds.length === 0) {
      setError(t("Please_select_at_least_one_manager"));
      setIsLoading(false);
      return;
    }

    try {
      // API call to update headset
      const targetHeadsetId = headsetId || headset.id;
      await updateHeadset(targetHeadsetId, headsetName, selectedAdminIds);
      
      const newAdminNames = selectedAdminIds.map(id => {
          const user = availableAdmins.find(u => u.id === id);
          if (user) return `${user.firstName} ${user.lastName}`;
          if (headset.adminIds && headset.adminNames) {
            const index = headset.adminIds.indexOf(id);
            if (index !== -1 && headset.adminNames[index]) {
                return headset.adminNames[index];
            }
          }
          return "Unknown";
      });

      const updatedHeadset = {
        ...headset,
        name: headsetName,
        adminIds: selectedAdminIds,
        adminNames: newAdminNames
      };

      handleChangeHeadsetData(updatedHeadset);
      
    } catch (err: any) {
      console.error("Update headset error:", err);
      setError(t("Failed_to_update_headset")); 
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="confirmation-modal">
      <div className="modal-content">
        <div style={{ textAlign: "center", fontSize: "1.3em", marginTop: "2vh" }}>
          {t("Edit_Headset")}
        </div>
        
        {isFetchingAdmins ? (
             <div style={{ textAlign: "center", padding: "20px" }}>{t("Loading...")}</div>
        ) : (
        <form onSubmit={handleSubmit} className="m-4">
          
          {/* Headset Name */}
          <div className="mb-3">
            <label htmlFor="headsetName" style={{ marginLeft: "20px", color: "#404040", fontWeight: "bold" }}>
              {t("Headset_Name")}
            </label>
            <input
              type="text"
              className="form-control"
              id="headsetName"
              value={headsetName}
              onChange={(e) => setHeadsetName(e.target.value)}
              style={{ backgroundColor: "#CCCCCC", borderRadius: "20px", color: "#404040", height: "5vh", maxHeight: "40px", fontSize: "1em", paddingLeft: "20px" }}
            />
          </div>

          {/* Headset Managers */}
          <div className="mb-3">
            <label style={{ marginLeft: "20px", color: "#404040", fontWeight: "bold" }}>
              {t("Headset_Manager")}
            </label>
            
            {/* List of selected admins */}
            <div style={{ marginBottom: "10px", marginLeft: "20px" }}>
              {selectedAdminIds.map(adminId => (
                <div key={adminId} style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
                  <span style={{ marginRight: "10px" }}>
                    {getAdminDisplayName(adminId)}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveAdmin(adminId)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#A63939",
                      fontWeight: "bold",
                      cursor: "pointer"
                    }}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>

            {/* Add admin dropdown */}
            <div style={{ display: "flex", alignItems: "center" }}>
              <select
                className="form-control"
                value=""
                onChange={(e) => {
                   const val = e.target.value;
                   if (val && !selectedAdminIds.includes(val)) {
                       setSelectedAdminIds([...selectedAdminIds, val]);
                   }
                }}
                style={{ 
                  backgroundColor: "#CCCCCC", 
                  borderRadius: "20px", 
                  color: "#404040", 
                  height: "5vh", 
                  maxHeight: "40px", 
                  fontSize: "1em", 
                  paddingLeft: "20px",
                  flex: 1,
                }}
              >
                <option value="" disabled hidden>{t("Select_admin_to_add")}</option>
                {availableAdmins
                  .filter(a => !selectedAdminIds.includes(a.id)) // Filter out already selected
                  .map(admin => (
                    <option key={admin.id} value={admin.id}>
                      {admin.firstName} {admin.lastName} ({t(admin.role)})
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {error && (
            <div
              style={{
                textAlign: "center",
                color: "#A63939",
                fontSize: "0.8em",
                marginBottom: "1vh",
              }}
            >
              {error}
            </div>
          )}
          <div className="button-container">
            <button
              type="button"
              className="btn btn-secondary buttonCancel"
              onClick={() => setShowChangeHeadsetDataModal(false)}
              disabled={isLoading}
            >
              {t("Cancel")}
            </button>
            <button type="submit" className="btn btn-primary buttonAcceptGreen" disabled={isLoading}>
              {isLoading ? t("Save") : t("Save")}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default ChangeHeadsetDataModal;